import { cn } from '@repo/tailwind-config/tailwind.functions'
import { ArrowLeftRight } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { getArrivalAndDepartures } from '../../../../api/arrivals-and-departures/get-arrival-and-departures'
import { getCenterArrivalDepartureTransfers } from '../../../../api/centers/center-arrival-departure-transfers/get-center-arrival-departure-transfers'
import { updateCenterArrivalDepartureTransfers } from '../../../../api/centers/center-arrival-departure-transfers/update-center-arrival-departure-transfers'
import { getCenter } from '../../../../api/centers/get-center'
import { getTripAddresses } from '../../../../api/trip-addresses/get-trip-addresses'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { ArrivalAndDepartureProperties } from '../../../../lib/types/models/arrival-and-departure/arrival-and-departure.properties'
import { Center } from '../../../../lib/types/models/center/center'
import {
  CenterArrivalDepartureTransfer,
  CenterArrivalDepartureTransferInput
} from '../../../../lib/types/models/center/center-arrival-departure-transfers'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import EditableTable, { EditableTableColumn } from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton,
  UploadInvoiceComponent,
  userInvoiceColorMapping
} from './center.ui'
import {
  centerArrivalDepartureTransfersEditableColumns,
  CenterCostsTableProps,
  getCenterArrivalDepartureTransfersColumns,
  mapCenterArrivalDepartureTransfersToInput
} from './utils'

export function CenterArrivalDepartureTransfersTable(
  props: DataProps<Center, CenterCostsTableProps>
) {
  const { mode, costType, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const [editable, setEditable] = useState(false)
  const [transfers, setTransfers] = useState<CenterArrivalDepartureTransferInput[]>()

  const {
    id: centerId,
    data: center,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { centerCostsView, lock, unlock, isPending, uploadInvoice, deleteInvoice } =
    useCenterCosts({ centerId, costType, table: 'transfers' })

  const { data: centerArrivalDepartureTransfers, refetch } = useQuery({
    queryKey: ['center-arrival-departure-transfers', centerId],
    queryFn: () => getCenterArrivalDepartureTransfers(centerId)
  })

  const { data: arrivalAndDepartures } = useQuery({
    queryKey: ['arrival-and-departures'],
    queryFn: () => getArrivalAndDepartures({ version: 'LIGHT', filter: { olId: user?.ol?.id } })
  })

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses', centerId],
    queryFn: () => getTripAddresses()
  })

  const { mutateAsync } = useMutation({
    mutationFn: (data: CenterArrivalDepartureTransferInput[]) =>
      updateCenterArrivalDepartureTransfers(centerId, data),
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      centerCostsView.refetch()
      setTransfers(undefined)
    }
  })

  const { editableColumns, tooltips } = centerArrivalDepartureTransfersEditableColumns[user.role]

  const columns: EditableTableColumn<CenterArrivalDepartureTransfer>[] = useMemo(
    () => [
      ...getCenterArrivalDepartureTransfersColumns(costType, currency, t, {
        arrivalAndDeparturesOptions:
          arrivalAndDepartures?.data?.map((ad) => ({
            label:
              user.role === RoleEnum.ADMIN
                ? ad.fromTo
                : ArrivalAndDepartureProperties.getLabel(ad),
            value: ad.id
          })) ?? [],
        tripAddressOptions:
          tripAddresses?.data?.map((address) => ({
            label: address.label,
            value: address.id
          })) ?? [],
        editableColumns,
        tooltips
      }),
      ...(costType === 'final-cost'
        ? ([
            {
              accessorKey: 'invoices',
              header: t('center.costs.tefl.teachers.and.leaders.table.header.upload.invoices'),
              centerCell: true,
              cell: ({ row }) => (
                <UploadInvoiceComponent
                  variant={userInvoiceColorMapping[user?.role ?? RoleEnum.Ol]}
                  invoice={row.original.invoice}
                  readOnly={(!editable && mode === 'action-edit') || mode === 'readonly'}
                  disabled={isPending}
                  onDelete={async () => {
                    await deleteInvoice.mutateAsync(row.original.id)
                    refetch()
                  }}
                  onChange={async (file) => {
                    await uploadInvoice.mutateAsync({ id: row.original.id, file })
                    refetch()
                  }}
                />
              )
            }
          ] as EditableTableColumn<CenterArrivalDepartureTransfer>[])
        : [])
    ],
    [
      costType,
      currency,
      t,
      arrivalAndDepartures,
      tripAddresses,
      editableColumns,
      tooltips,
      user.role,
      editable,
      mode,
      isPending,
      deleteInvoice.mutateAsync,
      uploadInvoice.mutateAsync,
      refetch
    ]
  )

  const handleDataChange = useCallback(
    (data: CenterArrivalDepartureTransfer[]) => {
      const mappedData = data.map((item) =>
        mapCenterArrivalDepartureTransfersToInput(item, costType)
      )
      if (mode === 'free-edit') {
        updateCenterArrivalDepartureTransfers(centerId, mappedData)
          .then(() => {
            refetch()
            centerCostsView.refetch()
            setTransfers(undefined)
          })
          .catch(showAxiosError)
      } else setTransfers(mappedData)
    },
    [costType, mode, centerId, centerCostsView.refetch, mapCenterArrivalDepartureTransfersToInput]
  )

  if (!centerArrivalDepartureTransfers || !center) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        mode={mode}
        icon={ArrowLeftRight}
        title={t('center.costs.arrival.departure.transfers.title')}
        onCancel={() => setTransfers(undefined)}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: center.isHostFamiliesLocked,
          onLock: async () => {
            await lock.mutateAsync()
          },
          onUnlock: async () => {
            await unlock.mutateAsync()
          }
        }}
        onSave={async () => {
          if (transfers) {
            await mutateAsync(transfers)
            await Promise.all([refetch(), centerCostsView.refetch(), refetchCenter?.()])
          }
        }}
      />

      <EditableTable<CenterArrivalDepartureTransfer>
        disableHover
        overflow={costType === 'final-cost' ? false : true}
        isEditable={editable}
        data={centerArrivalDepartureTransfers.data}
        columns={columns}
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={
              costType === 'final-cost'
                ? t('center.final.costs.fees')
                : t('center.costs.arrival.departure.transfers.table.footer.total.transfers.cost')
            }
            total={centerCostsView.data?.totalTransferCost}
            initialAmount={centerCostsView.data?.totalTransferCost}
            finalAmount={centerCostsView.data?.finalTotalTransferCost}
            balance={centerCostsView.data?.transferBalance}
          />
        }
        onDataChange={handleDataChange}
      />
    </div>
  )
}
